import { type Ref } from 'vue';
import gsap from 'gsap';

export default function useHorizontalScrollingNative({
  scrollContainer,
  onProgressCallback,
}: {
  scrollContainer: Ref<null | HTMLElement>;
  onProgressCallback: (p: number) => void;
}) {
  const scrollProgress = ref<number>(0);
  const { isMobileOrTablet } = useDevice();
  const isReady = ref<boolean>(false);

  const scrollTimeline = gsap.timeline({
    paused: true,
  });

  const { isVs } = useViewportSize();

  scrollTimeline.progress(0);

  onMounted(async () => {
    await nextTick();
    if (scrollContainer.value) {
      setTimeout(() => {
        isReady.value = true;
      }, 950);
      const container = scrollContainer.value;
      if (!isMobileOrTablet) {
        addWheelTrigger(container);
        addKeydownListener(container);
      } else {
        addScrollListener(container);
      }
    }

    window.addEventListener('resize', () => {
      if (scrollContainer.value) {
        setProgress(
          isVs.value
            ? scrollContainer.value.scrollTop
            : scrollContainer.value.scrollLeft,
        );
      }
    });

    document.documentElement.setAttribute(
      'data-scroll-direction',
      'horizontal',
    );
  });

  onUnmounted(() => {
    const container = scrollContainer.value;
    container?.scrollTo({
      left: 0,
    });
    if (container) {
      removeWheelTrigger(container);
      removeScrollListener(container);
      removeKeydownListener(container);
    }
  });

  function addWheelTrigger(ct: HTMLElement) {
    ct.addEventListener('wheel', (e) => wheelEvent(e, ct));
  }

  function removeWheelTrigger(ct: HTMLElement) {
    ct.removeEventListener('wheel', (e) => wheelEvent(e, ct));
  }

  function setProgress(scrollDistance: number) {
    const scrollerWidth = getScrollWidth();
    const scrollerHeight = getScrollHeight();
    let p = 0;
    if (isVs.value) {
      p = Math.min((100 / scrollerHeight) * scrollDistance);
    } else {
      p = Math.min((100 / scrollerWidth) * scrollDistance);
    }
    scrollProgress.value = p === Infinity ? 0 : p;
    scrollTimeline.progress(scrollProgress.value * 0.01);
    onProgressCallback(scrollProgress.value);
  }

  function getScrollWidth() {
    if (scrollContainer.value) {
      return scrollContainer.value?.scrollWidth - window.innerWidth;
    }
    return 0;
  }

  function getScrollHeight() {
    if (scrollContainer.value) {
      return scrollContainer.value?.scrollHeight - window.innerHeight;
    }
    return 0;
  }

  function addScrollListener(ct: HTMLElement) {
    ct.addEventListener('scroll', scrollEvent);
  }

  function removeScrollListener(ct: HTMLElement) {
    ct.removeEventListener('scroll', scrollEvent);
  }

  function addKeydownListener(ct: HTMLElement) {
    window.addEventListener('keyup', (e) => onKeyDown(e, ct));
  }

  function removeKeydownListener(ct: HTMLElement) {
    window.removeEventListener('keyup', (e) => onKeyDown(e, ct));
  }

  function onKeyDown(e: KeyboardEvent, el: HTMLElement) {
    if (e.key === ' ' || e.code === 'Space') {
      el.scrollBy({ top: isVs.value ? 250 : 0, left: isVs.value ? 0 : 250 });
      setProgress(isVs.value ? el.scrollTop : el.scrollLeft);
    }
  }

  function scrollEvent(event: Event) {
    const element = event.target as HTMLElement;
    setProgress(isVs.value ? element.scrollTop : element.scrollLeft);
  }

  function wheelEvent(event: WheelEvent, element: HTMLElement) {
    const d: number = event.deltaY;
    element.scrollBy({ top: isVs.value ? d : 0, left: isVs.value ? 0 : d });
    setProgress(isVs.value ? element.scrollTop : element.scrollLeft);
  }

  return {
    isReady,
  };
}
